import { useCallback, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { env } from '@/app/config/env.config';
import { getCurrentAccessToken } from '@/shared/api/api-client';
import { getCredits } from '../services/credits.service';
import type { CreditAccount } from '../models/credit.types';

interface UseCreditsResult {
  credits: CreditAccount | null;
  isLoading: boolean;
  error: unknown;
  reload: () => Promise<void>;
}

function socketOrigin(): string {
  try {
    return new URL(env.apiBaseUrl).origin;
  } catch {
    return env.apiBaseUrl;
  }
}

/**
 * Loads the authenticated user's AI-credit wallet and keeps it LIVE: the backend
 * pushes a `credits` event over the per-user `/notifications` socket whenever the
 * balance changes (consume / grant / plan / admin), so the top-bar chip updates
 * without a refresh. Stays null (silent) on error so a transient API hiccup never
 * breaks the shell chrome.
 */
export function useCredits(): UseCreditsResult {
  const [credits, setCredits] = useState<CreditAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setCredits(await getCredits());
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Live updates over the per-user notifications socket.
  useEffect(() => {
    const token = getCurrentAccessToken();
    if (!token) return;
    const socket = io(`${socketOrigin()}/notifications`, {
      auth: { token },
      transports: ['websocket'],
      autoConnect: true,
    });
    socket.on('credits', (next: CreditAccount) => {
      setCredits(next);
    });
    return () => {
      socket.off('credits');
      socket.disconnect();
    };
  }, []);

  return { credits, isLoading, error, reload };
}
